import { Lock, Umbrella } from '../icons.js';

import type { ProjectProgress } from '@nedelnik/shared';

/**
 * Отметки задачи снаружи, без её открытия.
 *
 * Все они значок плюс, если есть что считать, счётчик: в строке задачи их
 * стоит до пяти штук, и словами они занимали столько места, что вытесняли
 * заголовок. Значок молчит только для глаза — у каждой отметки есть подпись
 * `aria-label`, и она полная, а не сокращённая.
 *
 * Чипа с названием проекта здесь больше нет: владелец попросил убрать ссылку
 * из строки (замечания после этапа 12), и требование 3.8.6 переписано под это
 * — проект вложенной задачи виден в её карточке. Отметка с зонтиком осталась,
 * но она про другое: про задачу, которая **сама** является проектом.
 */

/** Задача — проект: зонтик и доля закрытых вложенных (3.8.5). */
export function ProjectBadge({
  progress,
}: {
  progress: ProjectProgress | undefined;
}): React.ReactElement | null {
  if (progress === undefined || progress.total === 0) return null;

  return (
    <span
      className="mark mark--project"
      // Роль обязательна: у `span` её нет, и подпись без роли в дерево
      // доступности не попадает вовсе — значок остался бы немым.
      role="img"
      aria-label={`Проект: закрыто ${String(progress.done)} из ${String(progress.total)}`}
      title={`Проект: закрыто ${String(progress.done)} из ${String(progress.total)}`}
    >
      <Umbrella />
      {progress.done}/{progress.total}
    </span>
  );
}

/**
 * Метка «заблокирована» (3.9.4) — теперь замок вместо слова.
 *
 * Именно метка, а не запрет: закрыть заблокированную задачу можно (3.9.5).
 * Подпись объясняет, что убирать её нечем: она гаснет сама, когда блокирующая
 * задача закрыта или связь снята в карточке. На телефоне `title` не
 * показывается вовсе, поэтому то же самое сказано и в `aria-label`.
 */
export function BlockedBadge({ blocked }: { blocked: boolean }): React.ReactElement | null {
  if (!blocked) return null;

  const explanation =
    'Задачу держит незакрытая зависимость. Закрыть её всё равно можно; ' +
    'снять связь — в карточке задачи, раздел «Связи»';

  return (
    <span className="mark mark--blocked" role="note" aria-label={explanation} title={explanation}>
      <Lock />
    </span>
  );
}
